// Pure helpers over the existing GET /api/validate-coupons/ response (billing_validate_coupons):
// { status, type: 'COUPON' | 'AFFILIATE_COUPONS' | 'AFFILIATE' | 'UNKNOWN', coupons: [...] }.
// Coupon fields: couponType ('Percent Off' | 'Amount Off' | 'Credits' | 'Period Free' |
// 'Credit Percentage Off'), amountOff, credits, freeDays, product ('ANY' | plan code), exceeded, isExpired.
import { formatPrice, round2 } from './plans'

export const COUPON_INVALID_MESSAGE = 'This promo code is not valid.'
export const COUPON_OTHER_PLAN_MESSAGE = 'This promo code is for a different plan.'
export const COUPON_EXCEEDED_MESSAGE = "This promo code's use limit has been exceeded."
export const COUPON_EXPIRED_MESSAGE = 'This promo code has expired.'
export const COUPON_CREDITS_ONLY_MESSAGE = 'This code applies to credit purchases, not plans.'

export function describeCoupon (coupon, unit) {
  if (!coupon) return ''
  switch (coupon.couponType) {
    case 'Percent Off':
      return `${coupon.amountOff}% off`
    case 'Amount Off':
      return `${formatPrice(coupon.amountOff)} off`
    case 'Credits':
      return `+${coupon.credits} credits`
    case 'Period Free':
      return `${coupon.freeDays} days free`
    default:
      return 'applied'
  }
}

export function discountedPrice (price, coupon) {
  if (!coupon) return round2(price)
  if (coupon.couponType === 'Percent Off') return round2(price * (100 - Number(coupon.amountOff || 0)) / 100)
  if (coupon.couponType === 'Amount Off') return Math.max(0, round2(price - Number(coupon.amountOff || 0)))
  return round2(price)
}

/**
 * Pick the coupon that applies to `planCode` from a validate-coupons response.
 * Returns { coupon, affiliateOnly, error }: affiliateOnly means a valid affiliate code with no discount.
 */
export function pickCouponForPlan (data, planCode) {
  if (!data || data.status !== 'SUCCESS' || data.type === 'UNKNOWN') {
    return { coupon: null, affiliateOnly: false, error: COUPON_INVALID_MESSAGE }
  }
  const coupons = (data.coupons || []).filter((c) => !c.isDeleted)
  const matching = coupons.filter((c) => !planCode || !c.product || c.product === 'ANY' || c.product === planCode)
  if (!matching.length) {
    if (data.type === 'AFFILIATE' || data.type === 'AFFILIATE_COUPONS') {
      return { coupon: null, affiliateOnly: true, error: coupons.length ? COUPON_OTHER_PLAN_MESSAGE : '' }
    }
    return { coupon: null, affiliateOnly: false, error: coupons.length ? COUPON_OTHER_PLAN_MESSAGE : COUPON_INVALID_MESSAGE }
  }
  const coupon = matching[0]
  if (coupon.exceeded) return { coupon: null, affiliateOnly: false, error: COUPON_EXCEEDED_MESSAGE }
  if (coupon.isExpired) return { coupon: null, affiliateOnly: false, error: COUPON_EXPIRED_MESSAGE }
  if (coupon.couponType === 'Credit Percentage Off') {
    return { coupon: null, affiliateOnly: false, error: COUPON_CREDITS_ONLY_MESSAGE }
  }
  return { coupon, affiliateOnly: false, error: '' }
}
